// A CHAT turn versus a WEB-SEARCH turn inside the one research conversation (busy-my-web#397).
//
// Saffet: "Our ongoing chat should be about the chat like brain storming with some links not look
// same as web search." An Ongoing's conversation holds two kinds of turn — a plain question the
// Brain answers from the story and the mailbox (`web: false`, busy-my-web#360), and the globe's web
// search — and until #397 the workspace drew both through the same pictures → prices → answer →
// source-cards layout, so a chat answer wore a results page's furniture.
//
// The rules that tell the two apart, and what a chat turn is allowed to show, live here as plain
// functions: the workspace reads them, the Briefing strip reads them, and neither has to hold an
// opinion of its own about what "a chat turn" is.

/** How many links a chat answer lists under itself — "some links", not a results grid. */
export const MAX_CHAT_LINKS = 3

/**
 * Is this turn the chat leg?
 *
 * Only an EXPLICIT `web: false` says so. A turn that carries no flag at all — every turn that
 * existed before #360, and a restored one from a server that does not store the flag — keeps the
 * results rendering it has always had: the conservative reading, because a web search drawn as a
 * chat would hide its pictures and prices, where a chat drawn as a search is merely what we had.
 */
export function isChatTurn(turn) {
  return !!turn && turn.web === false
}

/** The `[n]` citation numbers in the order the answer uses them, each once. */
export function citedIndexes(answer) {
  const seen = new Set()
  const out = []
  const re = /\[(\d{1,2}(?:\s*,\s*\d{1,2})*)\]/g
  let m
  while ((m = re.exec(String(answer || ''))) !== null) {
    for (const n of m[1].split(/\s*,\s*/)) {
      const i = Number(n) - 1
      if (i >= 0 && !seen.has(i)) { seen.add(i); out.push(i) }
    }
  }
  return out
}

/**
 * The short link list under a chat answer.
 *
 * The links the prose actually CITES come first, in the order it cites them — those are the ones
 * the reader has just been pointed at — then the rest of the turn's links in their own order. One
 * entry per URL (the same page cited twice is one link), capped at `max`. Videos are folded in
 * after the links: a chat may point at one, but it is a link here, not a card.
 *
 * @returns {Array} the turn's own link items, untouched — the ids are what the Work-board ticks by.
 */
export function chatLinks(turn, max = MAX_CHAT_LINKS) {
  const links = Array.isArray(turn?.links) ? turn.links : []
  const videos = Array.isArray(turn?.videos) ? turn.videos : []
  const ordered = []
  for (const i of citedIndexes(turn?.answer)) if (links[i]) ordered.push(links[i])
  for (const l of links) ordered.push(l)
  for (const v of videos) ordered.push(v)

  const seen = new Set()
  const out = []
  for (const l of ordered) {
    const key = String(l?.url || '').trim()
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(l)
    if (out.length >= max) break
  }
  return out
}

/**
 * The link a clicked anchor in the answer stands for.
 *
 * A citation chip and a `[text](url)` link both render as plain anchors inside the answer's HTML,
 * so the click only knows an href. When the turn holds that URL as one of its links the LINK is
 * returned — with its id, so keeping it ticks the same item the list would — otherwise a bare
 * `{ url, title }` built from the anchor itself. Nothing for a href that is not http(s).
 */
export function linkForHref(turn, href, text = '') {
  const url = String(href || '').trim()
  if (!/^https?:\/\//i.test(url)) return null
  const pool = [...(turn?.links || []), ...(turn?.videos || [])]
  const own = pool.find(l => String(l?.url || '').trim() === url)
  if (own) return own
  return { kind: 'link', url, title: String(text || '').trim() || url }
}

/**
 * What "open this link in Busy.me web search" ASKS.
 *
 * The page cannot be shown inside the app — most sites refuse to be framed — so opening a link
 * here means searching the web for it: its title when it has one (that is what the reader saw),
 * else the address itself, which the search understands as "about this page".
 */
export function searchQueryFor(link) {
  const title = String(link?.title || '').trim()
  const url = String(link?.url || '').trim()
  if (title && title !== url) return title
  return url
}

/** The two ways a chat link can be opened — the choice the reader is offered. */
export const LINK_ROUTES = Object.freeze({
  /** a new web-search turn on the same conversation, about this link */
  WEB: 'web',
  /** a new browser tab, with the smart sidebar briefing the link beside it */
  EXTERNAL: 'external',
})
